<template>
  <el-drawer
    title="Create New Payment Link"
    :visible.sync="showDrawer"
    :with-header="true"
    :before-close="handleClose"
    :size="drawerWidth"
  >
    <div class="mx-10">
      <el-form
        :model="createPaymentLinkForm"
        :rules="rules"
        label-position="top"
        ref="createPaymentLinkForm"
      >
        <!-- The id in the next line maps the walkthrough to that el-form-item element. Please do not remove this id -->
        <el-form-item
          label="Payment Link Title"
          prop="title"
          id="paymentLinkItem2"
        >
          <el-input
            v-model="createPaymentLinkForm.title"
            placeholder="Enter link name"
          />
        </el-form-item>
        <el-form-item label="Payment Link Description" prop="description">
          <el-input
            type="textarea"
            :autosize="{ minRows: 5, maxRows: 8 }"
            v-model="createPaymentLinkForm.description"
            placeholder="Enter link description"
          />
        </el-form-item>
        <div class="grid grid-cols-3 gap-x-4">
          <el-form-item label="Currency" class="col-span-1">
            <el-select
              v-model="createPaymentLinkForm.currency"
              placeholder="Select"
            >
              <i slot="prefix">
                <currency-icon
                  :currency="getCurrencyCountry(createPaymentLinkForm.currency)"
                  class="mr-12 ml-1"
              /></i>
              <el-option
                v-for="(currency, index) in filteredPaymentLinkCurrencies"
                :key="index"
                :label="currency.currency"
                :value="currency.currency"
              >
                <div class="flex items-center gap-x-2">
                  <currency-icon
                    :currency="getCurrencyCountry(currency.currency)"
                  />
                  <p class="text-fg-muted font-normal">
                    {{ currency.currency }}
                  </p>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            class="col-span-2"
            label="Amount To Receive"
            prop="amount"
          >
            <el-input
              v-model="createPaymentLinkForm.amount"
              @input="handleInput"
              :placeholder="`${currencySymbol}0.00`"
            />
          </el-form-item>
        </div>
        <!-- The id in the next line maps the walkthrough to that el-form-item element. Please do not remove this id -->
        <el-form-item label="" prop="feeBearer" id="paymentLinkItem3">
          <div class="flex items-center justify-between mt-4">
            <p class="font-light text-sm flex flex-col">
              <span class="font-semibold"
                >Charge Fee on Customer's Payment</span
              >
              <span class="text-xs"
                >Transaction fee will be added to your customer's bill</span
              >
            </p>
            <el-switch
              active-value="customer"
              inactive-value="business"
              v-model="createPaymentLinkForm.feeBearer"
            ></el-switch>
          </div>
        </el-form-item>
        <el-form-item label="" prop="collectAddress">
          <div class="flex items-center justify-between mt-4">
            <p class="font-light text-sm flex flex-col">
              <span class="font-semibold">Collect Customers Address</span>
              <span class="text-xs">Collect Customers Address</span>
            </p>
            <el-switch
              v-model="createPaymentLinkForm.collectAddress"
            ></el-switch>
          </div>
        </el-form-item>
        <el-form-item label="" prop="collectPhoneNumber">
          <div class="flex items-center justify-between mt-4">
            <p class="font-light text-sm flex flex-col">
              <span class="font-semibold">Collect Customers Phone Number</span>
              <span class="text-xs">Collect Customers Phone Number</span>
            </p>
            <el-switch
              v-model="createPaymentLinkForm.collectPhoneNumber"
            ></el-switch>
          </div>
        </el-form-item>
        <el-form-item label="" prop="type">
          <div class="flex items-center justify-between mt-4">
            <p class="font-light text-sm flex flex-col">
              <span class="font-semibold">One Time Payment</span>
              <span class="text-xs"
                >Disable this link immediately a customer uses it</span
              >
            </p>
            <el-switch
              active-value="one_time"
              inactive-value="recurring"
              v-model="createPaymentLinkForm.type"
            ></el-switch>
          </div>
        </el-form-item>
        <el-form-item>
          <div class="rounded-lg px-4 border py-1.5 mt-2 lg:py-3">
            <!-- The id in the next line maps the walkthrough to that div element. Please do not remove this id -->
            <div
              class="flex justify-between font-normal text-sm content-center items-center"
              id="paymentLinkItem4"
            >
              <span class="font-semibold">Confirmation</span>
              <span
                class="text-primary font-semibold text-xl cursor-pointer"
                @click="toggleShow('showConfirmation')"
                >{{ showConfirmation ? '-' : '+' }}</span
              >
            </div>
            <transition name="slide-fade">
              <div>
                <div class="flex flex-col w-full" v-if="showConfirmation">
                  <el-radio
                    class="py-2"
                    value="1"
                    label="1"
                    v-model="hasShowConfirmation"
                  >
                    Display This Page After Payment
                  </el-radio>
                  <div v-if="hasShowConfirmation === '1'" class="mt-2">
                    <el-form-item label="Custom Message" prop="message">
                      <el-input
                        type="textarea"
                        :autosize="{ minRows: 5, maxRows: 8 }"
                        v-model="createPaymentLinkForm.message"
                        placeholder="Type Message"
                      />
                    </el-form-item>
                  </div>
                  <!-- new ui update to be revisted  -->
                  <el-radio
                    class="py-2"
                    value="2"
                    label="2"
                    v-model="hasShowConfirmation"
                  >
                    Redirect to Website
                  </el-radio>
                  <div v-if="hasShowConfirmation === '2'" class="mt-2">
                    <el-form-item prop="websiteUrl">
                      <el-input
                        type="textarea"
                        :autosize="{ minRows: 5, maxRows: 8 }"
                        v-model="createPaymentLinkForm.websiteUrl"
                        placeholder="https://website.com"
                      />
                    </el-form-item>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </el-form-item>
        <el-form-item>
          <!-- The id in the next line maps the walkthrough to that el-button element. Please do not remove this id -->
          <el-button
            id="paymentLinkItem5"
            :loading="isLoading"
            :disabled="isLoading"
            @click="submitForm('createPaymentLinkForm')"
            class="bg-primary text-white w-full rounded-lg py-5 shadow-lg border-none mt-7"
            >{{ !isLoading ? 'Create link' : 'Creating link...' }}</el-button
          >
        </el-form-item>
      </el-form>
    </div>
    <div>
      <app-tour
        :tourDetails="paymentLinkTour"
        tourComponent="payment_link"
        :displayTour="displayTour"
        @stopTour="stopTour"
      />
    </div>
  </el-drawer>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import {
  FormatAmount,
  ValidateInput,
  GetCurrencyCountry,
  ParseAmount,
  RemoveNonDigitAndDecimal,
  ValidateWebsiteUrl
} from '@/utils/helpers'
import tenant from '@/mixins/tenant.js'
import getBalance from '@/mixins/getBalance.js'
import { PAYMENT_LINKS_TOUR } from '@/utils/constants'
export default {
  name: 'Drawer',
  mixins: [tenant, getBalance],
  data () {
    return {
      displayTour: false,
      paymentLinkTour: PAYMENT_LINKS_TOUR,
      breakpoints,
      isLoading: false,
      showConfirmation: false,
      hasShowConfirmation: '1',
      createPaymentLinkForm: {
        title: '',
        description: '',
        amount: '',
        message: '',
        type: 'one_time',
        collectPhoneNumber: false,
        collectAddress: false,
        feeBearer: 'business',
        websiteUrl: '',
        currency: ''
      },
      rules: {
        title: [
          {
            required: true,
            message: 'Please enter link title',
            trigger: 'blur'
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        description: [
          {
            required: true,
            message: 'Please enter link description',
            trigger: 'blur'
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        amount: [
          { validator: this.validateAmount, trigger: ['blur', 'change'] },
          {
            required: true,
            message: 'Enter amount',
            trigger: 'blur'
          }
        ],
        message: [{ validator: ValidateInput, trigger: ['blur', 'change'] }],
        websiteUrl: [
          { validator: ValidateWebsiteUrl, trigger: ['blur', 'change'] }
        ]
      }
    }
  },
  props: {
    showDrawer: {
      type: Boolean,
      required: true
    }
  },
  components: {
    AppTour: () => import('@/components/v2/app-tour/AppTour.vue'),
    CurrencyIcon: () => import('@/assets/icons/CurrencyIcons.vue')
  },
  computed: {
    currentTourIndex () {
      return this.$store.state.appTour.currentPaymentLinkTourIndex
    },
    drawerWidth () {
      return breakpoints.lgAndUp ? '33%' : breakpoints.mdAndUp ? '70%' : '90%'
    },
    currencySymbol () {
      const details = this.paymentLinkCurrencyOptions.find(
        (country) => country.currency === this.createPaymentLinkForm.currency
      )
      return details ? details.symbol : '$'
    }
  },
  methods: {
    startPaymentLinksTour () {
      this.$tours.paymentLinksTour.start(this.currentTourIndex)
    },
    stopTour () {
      this.$store.dispatch('appTour/updatePaymentLinkTourStatus', false)
      this.displayTour = false
      this.handleClose()
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    toggleShow (arg) {
      this[arg] = !this[arg]
    },
    handleInput () {
      // Remove any non-digit and non-decimal characters
      this.createPaymentLinkForm.amount = RemoveNonDigitAndDecimal(
        this.createPaymentLinkForm.amount
      )
    },
    formatAmount (val) {
      return FormatAmount(val)
    },
    handleClose () {
      this.$emit('closeDrawer')
      this.$store.dispatch(
        'transactions/setCreatePaymentLinkFromTransactions',
        false
      )
    },
    validateAmount (rule, value, callback) {
      if (!FormatAmount(value) || FormatAmount(value) < 1) {
        callback(new Error('Provide an amount'))
      } else {
        callback()
      }
    },
    submitForm (formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.createPaymentLinkForm.amount = FormatAmount(
            this.createPaymentLinkForm.amount
          )
          this.createPaymentLink(this.createPaymentLinkForm)
        } else {
          this.$notify({
            showClose: true,
            message: 'Please fill all inputs correctly',
            type: 'error'
          })
        }
      })
    },
    createPaymentLink (payload) {
      this.isLoading = true
      this.$store
        .dispatch('paymentLink/createPaymentLink', payload)
        .then(() => {
          this.isLoading = false
          this.$emit('successfulPaymentLink')
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  },
  watch: {
    'createPaymentLinkForm.amount': function (newValue) {
      const result = ParseAmount(newValue)
      this.createPaymentLinkForm.amount = `${this.currencySymbol}${result}`
    },
    deep: true
  },
  mounted () {
    if (this.$store.state.appTour.paymentLinkTourIsActive) {
      setTimeout(() => {
        this.startPaymentLinksTour()
      }, 0)
    }
  }
}
</script>

<style scoped>
>>> .el-drawer__header > :first-child {
  font-size: 1.1rem;
  color: var(--foreground-default);
  font-weight: 600;
}
>>> .el-drawer__header {
  color: var(--foreground-default);
  font-size: 1.2rem;
  padding-bottom: 1rem;
  padding-top: 2rem;
  padding-left: 1.4rem;
  font-weight: 700;
  border-bottom: 1px solid var(--border-main);
  margin-bottom: 20px;
}
>>> .el-drawer {
  background: var(--background-canvas) !important;
}

>>> .el-switch__core {
  background: var(--foreground-on-disabled);
  border-color: var(--foreground-on-disabled);
  width: 48px;
  border-radius: 24px;
  height: 24.5px;
}
>>> .el-switch.is-checked .el-switch__core {
  background: var(--color-primary);
  border-color: var(--color-primary);
}
>>> .el-switch__core:after {
  width: 20px;
  height: 20px;
}
>>> .el-switch.is-checked .el-switch__core::after {
  margin-left: -20px;
}
>>> .el-input__inner {
  background: var(--input-background);
  color: var(--input-text);
  height: 55px;
  border-radius: 8px;
}
>>> .el-input__placeholder {
  color: var(--input-placeholder);
}
>>> .el-form-item {
  margin-bottom: 10px;
}
>>> .el-input--prefix .el-input__inner {
  padding-left: 38px;
}
>>> .el-input__prefix {
  top: 14px;
}
>>> .el-radio__input.is-checked + .el-radio__label {
  color: var(--fg-default);
}
>>> .el-radio__inner {
  border: 1px solid #dcdfe6;
  border-radius: 100%;
  width: 20px;
  height: 20px;
  position: relative;
  cursor: pointer;
  display: inline-block;
}
>>> .el-textarea__inner {
  display: block;
  resize: vertical;
  padding: 10px 15px;
  box-sizing: border-box;
  font-size: inherit;
  border: 1px solid #dcdfe6;
  border-radius: 10px;
}
>>> .el-form--label-top .el-form-item__label {
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
}
>>> .el-radio__label {
  font-size: 15px;
  padding-left: 8px;
}
>>> .el-radio {
  padding-left: 3px;
  align-items: center;
  display: flex;
}
</style>
